import React, { FC, InputHTMLAttributes, useMemo, useRef, useState } from 'react';
import '../constants/const.css';
import FormInput from './formInput';

interface NumberInputProps<T> extends InputHTMLAttributes<HTMLInputElement> {
  formValueState: [T, React.Dispatch<React.SetStateAction<T>>];
  value: number | string;
  dataNumberType?: string;
  'data-key': number[];
  mathCalc?: (value: number) => number;

  // data-significant-digits 入力・表示の最大少数桁数。命名はsignificant-digits(有効桁数)ではなくfraction-digits(少数桁数)が妥当 影響大きめなのでリファクタリングは保留
}

// 言語上、扱うことができる最大の数を超えないように
// 本システムでこれより大きい数を入力するケースは無いと予想されるため、こちらの値に設定
const DEFAULT_MAX_INPUT: number = 1000000000;
const DEFAULT_MIN_INPUT: number = -1000000000;

const NumberInput: FC<NumberInputProps<any>> = (props) => {
  const refInput = useRef<HTMLInputElement>(null);
  const [isFocused, setFocus] = useState<boolean>(false);

  const numberWithComma = useMemo(
    () =>
      !/[0-9]/.test(props.value?.toString() || '') || isNaN(Number(props.value))
        ? props.value
        : props.mathCalc
        ? props
            .mathCalc(Number(props.value))
            .toLocaleString(undefined, { maximumFractionDigits: refInput.current?.dataset.significantDigits ? Number(refInput.current.dataset.significantDigits) : undefined })
        : Number(props.value).toLocaleString(undefined, {
            maximumFractionDigits: refInput.current?.dataset.significantDigits ? Number(refInput.current.dataset.significantDigits) : undefined,
          }),
    [props.value],
  );

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocus(true);
    if (props.onFocus) {
      props.onFocus(e);
    }
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocus(false);
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (props.dataNumberType === 'integer' && e.key === '.') {
      // 定数指定の場合はピリオド入力は無視
      e.preventDefault();
    }

    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  };

  const onFocusDummy = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocus(true);

    refInput.current?.focus();
  };

  const resolveValue = (value: any, inputValue: any, _element: HTMLElement) => {
    const element = refInput.current;

    if (!element) {
      return value;
    }

    const { min: _min, max: _max, type, dataset } = element;
    const min = _min === '' ? DEFAULT_MIN_INPUT : Number(_min);
    const max = _max === '' ? DEFAULT_MAX_INPUT : Number(_max);

    // 整数指定が有効
    const mustRoundingInteger = type === 'number' && dataset.numberType === 'integer';

    // 少数桁数
    const significantDigits = mustRoundingInteger ? 0 : Number(dataset.significantDigits) || 0;

    const fixDigits = (value: any) => {
      if (type !== 'number') {
        return value;
      }

      let newValue = String(value).replace(/^0+((\.)|$)/, '0$2');

      if (significantDigits > 0) {
        newValue = newValue.replace(new RegExp(`([0-9]+\\.[0-9]{${significantDigits}}).*`), '$1');
      } else {
        newValue = newValue.replace(new RegExp('([0-9]+)\\..*'), '$1');
      }

      return newValue;
    };

    const toNumber = (value: any) => (mustRoundingInteger ? Number(fixDigits(value)) : Number(fixDigits(Number(value).toFixed(significantDigits))));
    const valueNumber = toNumber(value);
    if (valueNumber < min) {
      value = min;
    } else if (valueNumber > max) {
      value = max;
    }

    // 0.001のような入力の途中に発生する0.0が0に変換され、小数以下が入力できなくなってしまうためその場合は文字として扱う
    const isTyping = !mustRoundingInteger && /[0-9]\.(0+)?/.test(inputValue);

    const resolveValue = Array.isArray(value) ? [...value] : isNaN(Number(value)) || isTyping ? fixDigits(value) : toNumber(value);
    console.log('resolved', [value, resolveValue]);
    if (element.value && !Array.isArray(value)) {
      element.value = resolveValue;
    }
    return resolveValue;
  };

  return (
    <div className="number-input-container" style={{ position: 'relative' }}>
      <input
        {...{
          ...props,
          id: props.id ? `dummy-${props.id}` : undefined,
          type: 'text',
          value: numberWithComma,
          className: [props.className, 'dummy-input', isFocused ? 'd-none' : ''].join(' ').trim(),
          style: { ...props.style, position: 'absolute', pointerEvents: 'none' },
        }}
        readOnly
        onFocus={onFocusDummy}
      />
      <FormInput formValueState={props.formValueState} data-key={props['data-key']} resolveValue={resolveValue}>
        <input
          ref={refInput}
          {...{
            ...props,
            type: 'number',
            style: { ...props.style, backgroundColor: isFocused ? undefined : 'transparent', color: isFocused ? undefined : 'transparent' },
          }}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
        />
      </FormInput>
    </div>
  );
};

export default NumberInput;
